import type { Profile } from "./schemas/profile.js";

// EQ-1-PIN-DEFAULT — the athlete elects which machine wins for one exercise at
// one place, so the resolver stops asking.
//
// ⭐ Why it exists (Antenore, 2026-08-20). With ONE named machine the resolver
// already leaves him on his usual untagged one (EQ-1-DEFAULT-WINS). With TWO
// it returns `choose` and interrupts EVERY time, and there was no way to say
// which one he actually wants. His words: *"come può fare un'atleta per forzare
// una o l'altra come default [...] preferisco usare la mia solita macchina,
// perché l'altra, quella taggata, non mi piace, in un'altra situazione potrei
// voler usare l'altra come default"*.
//
// ⛔ It is the MIRROR of the ✕, not an extension of it. `locationUnavailable`
// states what is NOT here; this states which of what IS here should stop being
// asked about. They are two arrays because a demotion must never elect and an
// election must never sink a row — `hasAvailableInstance` can make an exercise
// sink when every instance is declared missing, and a pin sharing that storage
// could trigger it as a side effect.
//
// ⛔ Absence still WINS over election, and the ordering that guarantees it is
// the caller's: narrow by `availableInstancesAt` first, apply the pin second.
// Stated here because it is the one invariant this module cannot enforce alone.
export type EquipmentDefault = NonNullable<Profile["equipmentDefault"]>;
export type EquipmentDefaultEntry = EquipmentDefault[number];

/**
 * What the athlete elected for one exercise at one place, if anything.
 *
 * `place === null` answers `undefined` rather than searching: with no place
 * there is no key, exactly as the ✕ is withheld without one.
 */
export const pinnedAt = (
  list: EquipmentDefault | undefined,
  place: string | null,
  exercise: string,
): EquipmentDefaultEntry | undefined =>
  place === null
    ? undefined
    : list?.find((e) => e.place === place && e.exercise === exercise);

/**
 * The elected instance as the pickers speak it: a slug, `null` for the virtual
 * Default, or `undefined` for "nothing elected".
 *
 * ⚠️ THREE-valued on purpose, and the two non-answers are different. `null` is
 * an ANSWER — the athlete elected the untagged Default and the resolver must
 * stop asking. `undefined` is silence, where the resolver's own rules apply
 * unchanged. Collapsing them would turn "my usual one, stop asking" back into
 * the prompt this feature exists to remove.
 */
export const pinnedInstance = (
  list: EquipmentDefault | undefined,
  place: string | null,
  exercise: string,
): string | null | undefined => {
  const entry = pinnedAt(list, place, exercise);
  if (!entry) return undefined;
  if (entry.unnamed === true) return null;
  return entry.instance;
};

/**
 * Elect one machine, replacing whatever was elected before for that pair.
 *
 * At most one winner per (place, exercise): electing B unelects A with no
 * separate gesture, which is what "default" means and what the ★ on a row
 * reads as. `instance: null` elects the virtual Default.
 */
export const pinDefault = (
  list: EquipmentDefault | undefined,
  args: {
    place: string;
    exercise: string;
    instance: string | null;
    at: string;
  },
): EquipmentDefault => [
  ...(list ?? []).filter(
    (e) => !(e.place === args.place && e.exercise === args.exercise),
  ),
  {
    place: args.place,
    exercise: args.exercise,
    ...(args.instance === null
      ? { unnamed: true as const }
      : { instance: args.instance }),
    at: args.at,
  },
];

/**
 * Un-elect: back to the resolver's own rules for that pair.
 *
 * ⛔ NOT the same as electing the Default. Un-electing restores the question;
 * electing the Default answers it. A ★ that only ever toggled between "machine
 * A" and "the Default" would leave no way back to being asked.
 */
export const unpinDefault = (
  list: EquipmentDefault | undefined,
  place: string,
  exercise: string,
): EquipmentDefault =>
  (list ?? []).filter(
    (e) => !(e.place === place && e.exercise === exercise),
  );
